import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table } from 'react-bootstrap';
import { Bert } from 'meteor/themeteorchef:bert';
import { filter, isEmpty, isEqual, orderBy } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import * as templateOps from '../../../api/AnnotationTemplates/annotation-template-operations';

import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import AnnotationTemplateElementEditor from '../AnnotationTemplateElementEditor';
import ItemList from '../ItemList';
import TableContent from '../TableContent';
import TagInput from '../TagInput';
import Validation from '../../containers/Validation';
import ViewAnnotationTemplate from '../../pages/ViewAnnotationTemplate';

const NAV_HEIGHT = 56; // pixels
const FOOTER_HEIGHT = 60; // pixels footer
const EDITOR_TEMPLATE_HEADER = 49; // pixels
const NAME_INPUT = 70 + 16; // pixels
const TAGS_INPUT = 78 + 16; // pixels
const ICON_INPUT = 70 + 15; // pixels
const SAVE_BUTTON_ROW = 38; // pixels
const FIXED_HEIGHT =
  NAV_HEIGHT +
  FOOTER_HEIGHT +
  EDITOR_TEMPLATE_HEADER +
  NAME_INPUT +
  TAGS_INPUT +
  ICON_INPUT +
  SAVE_BUTTON_ROW;
const ASCENDENT = 'asc';
const DESCENDENT = 'desc';
const ELEMENT = 'element';
const PREVIEW = 'preview';

class AnnotationTemplateEditor extends Component {
  state = {
    templateName: templateOps.getName(this.props.annotationTemplate),
    templateIcon: templateOps.getIcon(this.props.annotationTemplate),
    templateTags: templateOps.getTags(this.props.annotationTemplate),
    templateElements: templateOps.getElements(this.props.annotationTemplate),
    selectedElementIndex: -1,
    rightSideComponent: '',
    height: 0,
    suggestions: [],
    sort: true
  };

  componentDidMount() {
    this.updateSuggestions();
    this.updateWindowDimensions();
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentDidUpdate(prevProps) {
    const { annotationTemplate } = this.props;
    const { annotationTemplate: prevAnnotationTemplate } = prevProps;

    if (!isEqual(annotationTemplate, prevAnnotationTemplate)) this.updateSuggestions();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  updateWindowDimensions = () => this.setState({ height: window.innerHeight });

  handleTagDelete = tagIndex =>
    this.setState(prevState => ({
      templateTags: filter(prevState.templateTags, (tag, index) => index !== tagIndex)
    }));

  handleTagAddition = newTag =>
    this.setState(prevState => ({ templateTags: [...prevState.templateTags, newTag] }));

  handleElementDelete = elementIndex => {
    const { selectedElementIndex, sort } = this.state;
    if (selectedElementIndex === elementIndex) {
      this.handleChangeSelectedElement();
      this.setState({ rightSideComponent: '' });
    }
    this.setState(prevState => ({
      templateElements: filter(
        orderBy(prevState.templateElements, ['elementType'], [sort ? ASCENDENT : DESCENDENT]),
        (element, index) => index !== Number(elementIndex)
      )
    }));
    Bert.alert('Element deleted', 'danger');
  };

  handleElementAddition = newElement => {
    const { sort } = this.state;
    this.setState(prevState => {
      const newElements = orderBy(
        [...prevState.templateElements, newElement],
        ['elementType'],
        [sort ? ASCENDENT : DESCENDENT]
      );
      return {
        templateElements: newElements,
        selectedElementIndex: newElements.length - 1,
        rightSideComponent: 'element'
      };
    });
    Bert.alert('Element Added', 'success');
  };

  handleElementUpdate = (updatedElement, elementIndex) => {
    const { sort } = this.state;
    this.setState(prevState => {
      const newElements = orderBy(
        prevState.templateElements,
        ['elementType'],
        [sort ? ASCENDENT : DESCENDENT]
      );
      newElements[elementIndex] = updatedElement;
      return {
        templateElements: newElements,
        selectedElementIndex: elementIndex,
        rightSideComponent: 'element'
      };
    });
    Bert.alert('Element Updated', 'success');
  };

  handleChangeSelectedElement = (selectedElement = -1) =>
    this.setState(prevState => ({
      selectedElementIndex:
        prevState.selectedElementIndex === selectedElement ? -1 : selectedElement,
      rightSideComponent: prevState.selectedElementIndex === selectedElement ? '' : 'element'
    }));

  changeTemplateName = event => this.setState({ templateName: event.target.value });

  changeTemplateIcon = event => this.setState({ templateIcon: event.target.value });

  togglePreview = () =>
    this.setState(prevState => ({
      rightSideComponent: prevState.rightSideComponent === 'preview' ? '' : 'preview'
    }));

  showAddNewElement = () =>
    this.setState({ rightSideComponent: 'element', selectedElementIndex: -1 });

  cancelEditElement = () => this.setState({ rightSideComponent: '', selectedElementIndex: -1 });

  handleSubmit = async form => {
    const { annotationTemplate } = this.props;
    const { templateTags, templateElements, templateName, templateIcon } = this.state;

    const existingTemplate = templateOps.getId(annotationTemplate);

    const methodToCall = existingTemplate
      ? 'annotationTemplates.update'
      : 'annotationTemplates.insert';

    const newAnnotationTemplate = {
      name: templateName,
      icon: templateIcon,
      tags: templateTags,
      elements: templateElements
    };

    if (existingTemplate) newAnnotationTemplate._id = existingTemplate;

    try {
      await methodPromise(methodToCall, newAnnotationTemplate);
      this.redirectToAnnotationTemplatesView(form, existingTemplate);
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  handleSort = () => this.setState(prevState => ({ sort: !prevState.sort }));

  redirectToAnnotationTemplatesView(form, existingTemplate) {
    const { history } = this.props;
    const confirmation = existingTemplate ? 'Template updated!' : 'Template added!';
    form.reset();
    Bert.alert(confirmation, 'success');
    history.push(`/admin/annotationtemplates`);
  }

  async updateSuggestions() {
    const suggestions = await methodPromise('videos.getTagSuggestions');
    this.setState({ suggestions });
  }

  render() {
    const { annotationTemplate } = this.props;
    const {
      templateTags,
      selectedElementIndex,
      templateElements,
      rightSideComponent,
      templateIcon,
      templateName,
      height,
      suggestions,
      sort
    } = this.state;

    const elementEditorProps = {
      handleElementAddition: this.handleElementAddition,
      handleElementUpdate: this.handleElementUpdate,
      cancelEditElement: this.cancelEditElement,
      selectedElementIndex
    };

    const sortedTemplateElements = orderBy(
      templateElements,
      ['elementType'],
      [sort ? ASCENDENT : DESCENDENT]
    );

    const tableHeight = height - FIXED_HEIGHT;
    if (selectedElementIndex >= 0)
      elementEditorProps.templateElement = sortedTemplateElements[selectedElementIndex];

    const newAnnotationTemplate = {
      name: templateName,
      icon: templateIcon,
      tags: templateTags,
      elements: templateElements
    };

    const validationProps = {
      rules: {
        name: {
          required: true
        },
        icon: {
          required: true
        }
      },
      messages: {
        name: {
          required: 'Need a name in here, Seuss.'
        },
        icon: {
          required: 'This needs a model please'
        }
      },
      submitHandler: form => this.handleSubmit(form)
    };

    const listProps = {
      clickAction: 'select',
      isTemplateElementList: true,
      itemIdentifier: 'index',
      items: sortedTemplateElements,
      loading: false,
      datesColumns: false,
      selectedRow: selectedElementIndex,
      handleSelectRow: this.handleChangeSelectedElement,
      softDeleteItem: this.handleElementDelete
    };

    return (
      <Row>
        <Col xs={6}>
          <Validation {...validationProps}>
            <Form id="templateForm" onSubmit={event => event.preventDefault()}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={templateName}
                  onChange={this.changeTemplateName}
                  placeholder="Name you Template"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Tags</Form.Label>
                <TagInput
                  tags={templateTags}
                  suggestions={suggestions}
                  handleDelete={this.handleTagDelete}
                  handleAddition={this.handleTagAddition}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Icon</Form.Label>
                <Form.Control
                  type="text"
                  name="icon"
                  onChange={this.changeTemplateIcon}
                  value={templateIcon}
                  placeholder="Code for the fontawesome icon"
                />
              </Form.Group>
              <ItemList height={tableHeight}>
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th
                        className="align-middle"
                        style={{ cursor: 'pointer' }}
                        onClick={this.handleSort}
                      >
                        Template Elements ({templateElements.length})
                        {sort && <FontAwesomeIcon icon="sort-up" className="ml-1" />}
                        {!sort && <FontAwesomeIcon icon="sort-down" className="ml-1" />}
                      </th>
                      <th className="align-middle">
                        <Button variant="info" onClick={this.togglePreview} block>
                          <FontAwesomeIcon icon="eye" />
                        </Button>
                      </th>
                      <th className="align-middle">
                        <Button variant="success" onClick={this.showAddNewElement} block>
                          <FontAwesomeIcon icon="plus" />
                        </Button>
                      </th>
                    </tr>
                  </thead>
                  <TableContent
                    isTableEmpty={isEmpty(templateElements)}
                    itemName="Template Elements"
                    numberOfColumns={3}
                    {...listProps}
                  />
                </Table>
              </ItemList>
            </Form>
          </Validation>
        </Col>
        <Col xs={6}>
          {rightSideComponent === ELEMENT && (
            <AnnotationTemplateElementEditor {...elementEditorProps} />
          )}
          {rightSideComponent === PREVIEW && (
            <ViewAnnotationTemplate
              annotationTemplate={newAnnotationTemplate}
              readOnly
              selectedTemplate="one"
              {...this.props}
            />
          )}
        </Col>
        <Col xs={12}>
          <Button type="submit" variant="success" form="templateForm">
            {templateOps.getId(annotationTemplate) ? 'Save Changes' : 'Add Template'}
          </Button>
        </Col>
      </Row>
    );
  }
}

AnnotationTemplateEditor.defaultProps = {
  annotationTemplate: {
    name: '',
    icon: '',
    tags: [],
    elements: []
  },
  annotationTemplates: []
};

AnnotationTemplateEditor.propTypes = {
  annotationTemplate: PropTypes.object,
  annotationTemplates: PropTypes.arrayOf(PropTypes.object),
  history: PropTypes.object.isRequired
};

export default AnnotationTemplateEditor;
